import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from './auth/auth.service';
import { ParaclinicosPageComponent } from './pages/expediente/expediente-digital/sections/paraclinicos/paraclinicos-page/paraclinicos-page.component';
import { Router } from '@angular/router';

/**
 * Guard de autenticación funcional que protege las rutas
 * redirigiendo a los usuarios no autenticados al login
 */
export const authGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isLoggedIn()) {
    if (authService.isReceptionist()) return router.parseUrl('/agenda');
    if (authService.isAdmin()) return router.parseUrl('/agenda');
    else return true;
  }

  // Redirigir al login
  return router.parseUrl('/login');
};

export const authReception = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isLoggedIn()) {
    return true;
  }

  // Redirigir al login
  return router.parseUrl('/login');
};

/**
 * Guard para redirigir usuarios ya autenticados lejos del login/registro
 */
export const publicGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isLoggedIn()) {
    return true;
  }

  // Redirigir a welcome u otra ruta principal
  return router.parseUrl('/welcome');
};

/**
 * Guard de roles para limitar acceso a ciertas rutas
 * @param roles Arreglo de roles permitidos
 */
export const roleGuard = (roles: string[]) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Primero verificar si está autenticado
  if (!authService.isLoggedIn()) {
    return router.parseUrl('/login');
  }

  // Luego verificar si tiene alguno de los roles requeridos
  const hasRequiredRole = roles.some((role) => authService.hasRole(role));

  if (hasRequiredRole) {
    return true;
  }

  // Si no tiene el rol necesario, redirigir a una página de acceso denegado
  // o a su página principal
  return router.parseUrl('/welcome');
};

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () => import('./auth/login/login.page').then((m) => m.LoginPage),
    canActivate: [publicGuard],
  },
  {
    path: 'pre-registro/:id',
    loadComponent: () => import('./pages/pre-registro/pre-registro.page').then((m) => m.PreRegistroPage),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./auth/forgot-password/forgot-password.page').then((m) => m.ForgotPasswordPage),
    canActivate: [publicGuard],
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./auth/reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
    canActivate: [publicGuard],
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./pages/dashboard/dashboard.page').then((m) => m.DashboardPage),
    canActivate: [authGuard],
  },
  {
    path: 'welcome',
    loadComponent: () => import('./pages/welcome/welcome.page').then((m) => m.WelcomePage),
    canActivate: [authGuard],
  },
  {
    path: 'patient-billing',
    loadComponent: () => import('./pages/patient-billing/patient-billing.page').then((m) => m.PatientBillingPage),
  },
  {
    path: 'recetas',
    loadComponent: () => import('./pages/nutrition/menus/menus.component'),
  },
  {
    path: 'historial',
    loadComponent: () =>
      import('./shared/components/historial-consultas/historial-consultas.component').then(
        (m) => m.HistorialConsultasComponent,
      ),
  },
  {
    path: 'nutricion',
    loadComponent: () => import('./pages/nutricion/nutricion.page').then((m) => m.NutricionPage),
  },
  {
    path: 'nutricion/historia',
    loadComponent: () => import('./pages/nutrition/medical-history/medical-history.component').then((m) => m.default),
  },
  {
    path: 'remisiones',
    loadComponent: () => import('./pages/remisiones/remisiones.page').then((m) => m.RemisionesPage),
  },
  {
    path: 'nutricion-parenteral',
    loadComponent: () =>
      import('./pages/nutricion/nutricion-parenteral/nutricion-parenteral.page').then((m) => m.NutricionParenteralPage),
  },
  {
    path: 'nutricion-tamizajes',
    loadComponent: () =>
      import('./pages/nutricion/nutricion-tamizajes/nutricion-tamizajes.page').then((m) => m.NutricionTamizajesPage),
  },
  {
    path: 'nutricion-tx',
    loadComponent: () => import('./pages/nutricion/nutricion-tx/nutricion-tx.page').then((m) => m.NutricionTxPage),
  },
  {
    path: 'nutricion-antropometricos',
    loadComponent: () =>
      import('./pages/nutricion/nutricion-anthropometric/nutricion-anthropometric.page').then(
        (m) => m.NutricionAnthropometricPage,
      ),
  },
  {
    path: 'nutricion-bioquimicos',
    loadComponent: () =>
      import('./pages/nutricion/nutricion-bioquimicos/nutricion-bioquimicos.page').then(
        (m) => m.NutricionBioquimicosPage,
      ),
  },
  {
    path: 'nutricion-enteral',
    loadComponent: () =>
      import('./pages/nutricion/nutricion-enteral/nutricion-enteral.page').then((m) => m.NutricionEnteralPage),
  },
  {
    path: 'pagos/:id',
    loadComponent: () => import('./pages/payment/payment.page').then((m) => m.PaymentPage),
  },
  {
    path: 'agendar-cita',
    loadComponent: () => import('./pages/agendar-cita/agendar-cita.page').then((m) => m.AgendarCitaPage),
  },
  {
    path: 'agenda/dia',
    loadComponent: () => import('./adenda/dia/dia.page').then((m) => m.DiaPage),
  },
  {
    path: 'agenda',
    loadComponent: () => import('./calendario-citas/calendario-citas.page').then((m) => m.CalendarioCitasPage),
  },
  {
    path: 'calendario-rentas',
    loadComponent: () => import('./pages/rentas/calendario-rentas.page').then((m) => m.CalendarioRentasPage),
  },
  {
    path: 'catalogos-rentas',
    loadComponent: () => import('./pages/rentas/rentas-catalogos.page').then((m) => m.RentasCatalogosPage),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./auth/reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    path: 'settings',
    loadComponent: () => import('./pages/settings/settings.page').then((m) => m.SettingsPage),
  },
  {
    path: 'pacientes',
    loadComponent: () => import('./pages/pacientes/pacientes.page').then((m) => m.PacientesPage),
  },
  {
    path: 'staff',
    loadComponent: () => import('./pages/staff/staff.page').then((m) => m.StaffPage),
  },
  {
    path: 'equipos',
    loadComponent: () => import('./pages/equipos/equipos.page').then((m) => m.EquiposPage),
  },
  {
    path: 'reportes',
    loadComponent: () => import('./pages/reportes/reportes.page').then((m) => m.ReportesPage),
  },
  {
    path: 'equipment-detail/:id',
    loadComponent: () => import('./pages/equipment-detail/equipment-detail.page').then((m) => m.EquipmentDetailPage),
  },
  {
    path: 'start-procedure/:id',
    loadComponent: () => import('./pages/start-procedure/start-procedure.page').then((m) => m.StartProcedurePage),
  },
  {
    path: 'empresas',
    loadComponent: () => import('./pages/empresas/empresas.page').then((m) => m.EmpresasPage),
  },
  {
    path: 'empresas/new',
    loadComponent: () => import('./pages/empresas/empresas-form.page').then((m) => m.EmpresasFormPage),
  },
  {
    path: 'empresas/:id',
    loadComponent: () => import('./pages/empresas/empresas-form.page').then((m) => m.EmpresasFormPage),
  },
  {
    path: 'servicios',
    loadChildren: () => import('./pages/servicios/servicios.routes').then((m) => m.serviciosRoutes),
  },
  {
    path: 'crear-servicio',
    loadComponent: () =>
      import('./pages/servicios/crear-servicio/crear-servicio.page').then((m) => m.CrearServicioPage),
  },

  // ==================== RUTAS DE EXPEDIENTE ====================
  {
    path: 'expediente',
    loadComponent: () => import('./pages/expediente/expediente.page').then((m) => m.ExpedientePage),
    canActivate: [authReception],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'proximos',
      },
      {
        path: 'proximos',
        loadComponent: () =>
          import('./pages/expediente/pacientes-proximos/pacientes-proximos.component').then(
            (m) => m.PacientesProximosComponent,
          ),
      },
      {
        path: 'consulta',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/expediente-digital.page').then((m) => m.ExpedienteDigitalPage),
      },
      {
        path: 'consulta-legacy',
        loadComponent: () =>
          import('./pages/expediente/contenido-principal/contenido-principal.component').then(
            (m) => m.ContenidoPrincipalComponent,
          ),
      },
      {
        path: 'paraclinicos/:patientId',
        component: ParaclinicosPageComponent,
      },
      // ==================== 5 VISTAS DE EXPEDIENTE DIGITAL ====================
      {
        path: 'expediente-digital/consulta-medica',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/consulta-medica/consulta-medica.page').then(
            (m) => m.ConsultaMedicaPage,
          ),
      },
      {
        path: 'expediente-digital/nota-urgencias',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/nota-urgencias/nota-urgencias.page').then(
            (m) => m.NotaUrgenciasPage,
          ),
      },
      {
        path: 'expediente-digital/nota-ingreso',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/nota-ingreso/nota-ingreso.page').then((m) => m.NotaIngresoPage),
      },
      {
        path: 'expediente-digital/nota-evolucion',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/nota-evolucion/nota-evolucion.page').then(
            (m) => m.NotaEvolucionPage,
          ),
      },
      {
        path: 'expediente-digital/nota-egreso',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/nota-egreso/nota-egreso.page').then((m) => m.NotaEgresoPage),
      },
      // ==================== CENSO HOSPITALARIO ====================
      {
        path: 'censo-hospitalario',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/censo-hospitalario/censo-hospitalario.page').then(
            (m) => m.CensoHospitalarioPage,
          ),
      },
      // ==================== EXPEDIENTE COMPLETO ====================
      {
        path: 'expediente-completo',
        loadComponent: () =>
          import('./pages/expediente/expediente-digital/expediente-completo/expediente-completo.page').then(
            (m) => m.ExpedienteCompletoPage,
          ),
      },
      // ==================== ✅ NUEVA RUTA: DIRECTORIO MÉDICO ====================
      {
        path: 'directorio-medico',
        loadComponent: () =>
          import('./pages/expediente/directorio-medico/directorio-medico.page').then((m) => m.DirectorioMedicoPage),
      },
    ],
  },

  // Redirects
  {
    path: '**',
    redirectTo: 'welcome',
  },
];
